/**
 * Financial years, and the Annual Financial Return each one owes.
 *
 * The thing this module exists to stop: a company carrying one `afr_due_date`
 * that is overwritten every time a return is filed, so the year just filed is
 * destroyed by the year now open. A register like that cannot answer "who has
 * filed FY2025-26" — the companies that have show next year's dates, the ones
 * that have not show this year's, and nothing on the row says which it is.
 *
 * So a financial year is a *row* — see `CompanyAfrFiling` — and this module is
 * the arithmetic those rows are built from. Everything here is pure: the same
 * functions run in the Lambda that rolls a period, in the browser that previews
 * the roll, and in the importer that seeds the first one, and none of them may
 * disagree about what next year's dates are.
 *
 * ## The label is derived from the dates, never from the jurisdiction
 *
 * India writes `2026-27` and the BVI writes `2026`, and it is tempting to key
 * that off `companies.jurisdiction`. That column is the wrong source twice over:
 * it is a country *name* that falls back to a default when blank and accepts
 * unknown values with a warning, so the least-validated column in the register
 * would be deciding how every export reads. And it is not the actual
 * determinant. India writes two years because the Indian financial year *spans*
 * two calendar years; a 31 December year-end starts and ends in the same one.
 *
 * Deriving from the dates gets the case that matters right without anyone
 * thinking about it: a BVI-incorporated company filing on an Indian financial
 * year — routine, and close to the point of an offshore structure — labels as
 * `2026-27`, which a jurisdiction mapping would get wrong every single year.
 * It also self-corrects, because moving a company's year-end changes the label
 * format on its own rather than waiting for somebody to update a second field.
 *
 * Only the punctuation is configurable, and it is one house-wide setting rather
 * than a per-country one: how Somasi writes a label is Somasi's business.
 */

import type { FyLabelFormat } from "./enums.js";

/** A financial year, and where its return stands. The shape the roll moves. */
export interface AfrPeriod {
  /** Jurisdiction-neutral label, derived — `2026-27`, or `2026`. */
  fy_label: string;
  /** First day of the financial year — `2026-04-01`. */
  period_start: string;
  /** Last day of it, and the canonical sort and filter key — `2027-03-31`. */
  period_end: string;
  afr_due_date: string;
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_DAY = /^(\d{2})-(\d{2})$/;

/** Days in a month, Gregorian, with February resolved for the given year. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function parseDate(value: string, what: string): [number, number, number] {
  const match = DATE.exec(value.trim());
  if (!match) throw new Error(`${what} must be a date as YYYY-MM-DD, not "${value}"`);
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new Error(`${what} is not a real date: "${value}"`);
  }
  return [year, month, day];
}

function formatDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * A company's recurring year-end, as `MM-DD` — `03-31` for India, `12-31` for a
 * calendar year.
 *
 * Replaces the free-text `financial_year_end` the form used to collect
 * ("e.g. 31 December"), which could not be sorted, filtered or added to.
 *
 * 29 February is refused rather than clamped. It is a year-end that exists in
 * one year out of four, so every roll off it would have to invent a different
 * date — and a company whose accounts really close on the 29th closes them on
 * the 28th in three years out of four, which is what it should be stored as.
 */
export function validateFyEndMd(value: string): string {
  const match = MONTH_DAY.exec(value.trim());
  if (!match) throw new Error(`A financial year-end must read MM-DD, not "${value}"`);

  const [month, day] = [Number(match[1]), Number(match[2])];
  if (month < 1 || month > 12) throw new Error(`"${value}" is not a month`);
  // 2001 — any non-leap year, so February allows 28 days and not 29.
  if (day < 1 || day > daysInMonth(2001, month)) {
    throw new Error(`"${value}" is not a day of that month`);
  }
  return `${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * The same day of the month, `years` later or earlier, with the day clamped to
 * the month it lands in.
 *
 * Clamped rather than overflowed because `Date.UTC(2025, 1, 29)` is 1 March,
 * and a year-end that silently moves into the next month moves the financial
 * year with it. The clamp only ever fires on 29 February, which
 * {@link validateFyEndMd} already refuses as a year-end — it is reachable from
 * a backfilled historical period, where 28 February is the right answer.
 */
export function addYears(date: string, years: number): string {
  const [year, month, day] = parseDate(date, "date");
  const target = year + years;
  return formatDate(target, month, Math.min(day, daysInMonth(target, month)));
}

/** The day after a date. */
export function nextDate(date: string): string {
  const [year, month, day] = parseDate(date, "date");
  const at = new Date(Date.UTC(year, month - 1, day) + 86_400_000);
  return at.toISOString().slice(0, 10);
}

/**
 * The first day of the financial year that ends on `periodEnd`.
 *
 * A year back and a day on: `2027-03-31` opens on `2026-04-01`, `2026-12-31` on
 * `2026-01-01`.
 */
export function periodStartFor(periodEnd: string): string {
  return nextDate(addYears(periodEnd, -1));
}

/**
 * How a financial year reads.
 *
 * One calendar year in it, one number; two, and both are shown — always behind
 * `FY`, so a column of them scans as one list rather than as years and ranges
 * mixed together. A first-year
 * stub that runs long — incorporated November 2024, first year-end March 2026 —
 * falls out of the same rule as `2024-26`, which is both accurate and a useful
 * sign that something unusual happened.
 */
export function fyLabel(
  periodStart: string,
  periodEnd: string,
  format: FyLabelFormat = "short_dash",
): string {
  const [startYear] = parseDate(periodStart, "period_start");
  const [endYear] = parseDate(periodEnd, "period_end");
  if (endYear < startYear) {
    throw new Error(`A financial year cannot end before it starts: ${periodStart}…${periodEnd}`);
  }
  if (startYear === endYear) return `${FY_PREFIX}${startYear}`;

  const tail = format === "long_dash" ? String(endYear) : String(endYear).slice(2);
  return `${FY_PREFIX}${startYear}${format === "slash" ? "/" : "-"}${tail}`;
}

/**
 * The prefix every label carries.
 *
 * A bare `2026` beside a `2026-27` reads as two different kinds of thing — one
 * a year, the other a range — when they are both the same thing: a company's
 * financial year. The prefix says which, and makes a column of labels scan as
 * one list however many of them happen to span two calendar years.
 *
 * It is part of the stored label rather than added at render, so the value in
 * an export, in a filter and on screen are one string. A label read off a
 * spreadsheet should be the label you can paste into the search box.
 */
const FY_PREFIX = "FY";

/**
 * A stored label with the prefix guaranteed.
 *
 * Rows written before the prefix existed hold a bare `2026-27`, and a filter
 * listing both forms would offer what looks like two different years. Applied
 * on read, so those rows read correctly without being rewritten.
 */
export function displayFyLabel(label: string | null | undefined): string {
  if (!label) return "";
  return label.startsWith(FY_PREFIX) ? label : `${FY_PREFIX}${label}`;
}

/**
 * The financial year that ends on `periodEnd`, with the due date given.
 *
 * The first period of a company's history is built with this — the due date is
 * typed in once by whoever onboards it, and every later one is carried forward
 * by {@link rollPeriod}.
 */
export function afrPeriod(
  periodEnd: string,
  afrDueDate: string,
  format?: FyLabelFormat,
): AfrPeriod {
  const periodStart = periodStartFor(periodEnd);
  parseDate(afrDueDate, "afr_due_date");
  return {
    fy_label: fyLabel(periodStart, periodEnd, format),
    period_start: periodStart,
    period_end: periodEnd,
    afr_due_date: afrDueDate,
  };
}

/**
 * How long after its year-end a first AFR year is due, when nobody has said
 * otherwise — the BVI's nine months.
 *
 * Only a prefill: the first due date stays the human's answer and is editable
 * before it is saved, and every later year still inherits it plus a year.
 */
export const DEFAULT_AFR_DUE_MONTHS = 9;

/**
 * The due date a first AFR year is offered, `months` after its year-end.
 *
 * A year-end on the last day of its month is due on the last day of the month
 * it lands in — 30 June runs to 31 March, not 30 March — because "within nine
 * months of the year-end" counts whole months. Any other day keeps its day,
 * clamped to the month it lands in.
 */
export function defaultAfrDueDate(
  periodEnd: string,
  months: number = DEFAULT_AFR_DUE_MONTHS,
): string {
  const [year, month, day] = parseDate(periodEnd, "period_end");
  const index = year * 12 + (month - 1) + months;
  const [targetYear, targetMonth] = [Math.floor(index / 12), (index % 12) + 1];
  const lastDay = daysInMonth(targetYear, targetMonth);
  const monthEnd = day === daysInMonth(year, month);
  return formatDate(targetYear, targetMonth, monthEnd ? lastDay : Math.min(day, lastDay));
}

/**
 * The financial year that ends on or after `on`, for a company with this
 * year-end.
 *
 * Used to seed a company that has a year-end but no period yet, and to offer the
 * importer a `period_end` when the sheet gives only a due date.
 */
export function periodEndOnOrAfter(fyEndMd: string, on: string): string {
  const [month, day] = validateFyEndMd(fyEndMd).split("-").map(Number);
  const [year] = parseDate(on, "date");
  const thisYear = formatDate(year, month, Math.min(day, daysInMonth(year, month)));
  return thisYear >= on ? thisYear : addYears(thisYear, 1);
}

/**
 * Why a financial year cannot be a company's first, or null when it can.
 *
 * A year that closed before the company was incorporated is a year it never
 * had, so it cannot owe a return for it. The year the incorporation falls in
 * is the earliest — see {@link periodEndOnOrAfter}, which is how the form picks
 * it as the default. Shared so the form refuses for the reason the API gives.
 */
export function firstAfrYearProblem(
  periodEnd: string,
  incorporationDate: string | null | undefined,
): string | null {
  if (!incorporationDate || periodEnd >= incorporationDate) return null;
  return `The first AFR year can't end before the company was incorporated (${incorporationDate})`;
}

/**
 * The financial year that ends on or before `on`, for a company with this
 * year-end.
 *
 * The inverse of {@link periodEndOnOrAfter}, and what turns an imported *due
 * date* into the year it is the deadline for: a 30 September 2026 deadline on a
 * 31 March year-end is the return for the year ending 31 March 2026, because the
 * deadline always falls after the year it closes.
 */
export function periodEndOnOrBefore(fyEndMd: string, on: string): string {
  const [month, day] = validateFyEndMd(fyEndMd).split("-").map(Number);
  const [year] = parseDate(on, "date");
  const thisYear = formatDate(year, month, Math.min(day, daysInMonth(year, month)));
  return thisYear <= on ? thisYear : addYears(thisYear, -1);
}

/**
 * The first year-end strictly after a date, for a company with this year-end.
 *
 * What the roll needs when a company *changes* its financial year-end. Adding a
 * year to the stored date cannot see such a change: a company moved from 29 July
 * to 31 December would go on producing 29 July years for ever, and every
 * deadline derived from them would be for a financial year the company no longer
 * has.
 *
 * Measured from the period being closed rather than from today, so the series
 * stays contiguous — and a changed rule produces a short transitional period,
 * which is exactly what happens in reality when a company shortens its year.
 */
export function periodEndAfter(fyEndMd: string, after: string): string {
  const next = periodEndOnOrAfter(fyEndMd, after);
  return next > after ? next : addYears(next, 1);
}

/**
 * Next year's period, from the one just filed.
 *
 * Both dates move by exactly a year, which is the whole rule — there is no
 * stored offset between the year-end and the due date, and no per-jurisdiction
 * table of deadlines. The first due date is a human's answer and every later one
 * inherits it, so a registry that moves its deadline is handled by editing that
 * one period's due date and letting the next roll carry the new one forward.
 *
 * Derived from the closed period rather than from today, so a company filing
 * three years late rolls 2022-23 → 2023-24 and not straight to the current year.
 */
export function rollPeriod(
  period: AfrPeriod,
  /**
   * The company's year-end rule, where it is known.
   *
   * Given, the next year ends on the first occurrence of it after this year —
   * which is the same date as a plain year later while the rule is unchanged,
   * and the right date when somebody has moved it. Omitted, the stored date
   * simply gains a year, which is all the older call sites can do.
   */
  fyEndMd?: string | null,
  format?: FyLabelFormat,
): AfrPeriod {
  const periodEnd = fyEndMd
    ? periodEndAfter(fyEndMd, period.period_end)
    : addYears(period.period_end, 1);
  // The day after the year that just closed, not a year back from the new end.
  // On an unchanged rule those are the same date; on a changed one the second
  // would overlap the period just filed, double-counting months of trading.
  const periodStart = nextDate(period.period_end);

  return {
    fy_label: fyLabel(periodStart, periodEnd, format),
    period_start: periodStart,
    period_end: periodEnd,
    // The deadline moves with the year it belongs to. Where a changed year-end
    // has produced a short transitional period this is a starting point rather
    // than an answer — nothing here knows what the registry will give for a
    // stub year — so the dialog shows it and it is editable like any other.
    afr_due_date: addYears(period.afr_due_date, 1),
  };
}

/**
 * Why a filing date was refused, or null if it stands.
 *
 * The due date is no limit. A return filed late is still filed, and refusing
 * the real date leaves only two ways to record it — a false one inside the
 * deadline, or not at all — when the register is the place a late filing has to
 * show. The export reports how many days late it was.
 */
export function filingDateProblem(
  filingDate: string,
  period: Pick<AfrPeriod, "period_end">,
  today: string,
): string | null {
  try {
    parseDate(filingDate, "afr_filing_date");
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }

  // A return for a year that has not ended yet cannot have been filed, whatever
  // the registry's portal accepted.
  if (filingDate < period.period_end) {
    return `A return for the year ending ${period.period_end} cannot be filed before that date`;
  }
  if (filingDate > today) return "A filing date cannot be in the future";
  return null;
}

/** Where an open period stands against today — computed, never stored. */
export type AfrUrgency = "overdue" | "due_soon" | "upcoming";

/**
 * How urgent an open period is.
 *
 * `horizonDays` is a setting rather than a literal, for the same reason the
 * payroll divisors are: "due soon" is a business judgement about how far ahead
 * the team wants to look, and it changes.
 */
export function afrUrgency(afrDueDate: string, today: string, horizonDays: number): AfrUrgency {
  if (afrDueDate < today) return "overdue";
  const horizon = new Date(Date.parse(`${today}T00:00:00Z`) + horizonDays * 86_400_000)
    .toISOString()
    .slice(0, 10);
  return afrDueDate <= horizon ? "due_soon" : "upcoming";
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** `03-31` as "31 March" — the wording the form used to ask people to type. */
export function formatFyEnd(md: string | null | undefined): string {
  if (!md) return "";
  const match = MONTH_DAY.exec(md.trim());
  if (!match) return md;
  return `${Number(match[2])} ${MONTH_NAMES[Number(match[1]) - 1] ?? match[1]}`;
}

/** The days a month can end on, for the year-end picker. February stops at 28. */
export function daysInFyMonth(month: number): number {
  return daysInMonth(2001, month);
}

/** The month names, indexed from 1, for the year-end picker and the importer. */
export function fyMonthNames(): readonly string[] {
  return MONTH_NAMES;
}

/**
 * A year-end written as prose, back to `MM-DD` — `"31 December"`, `"Dec 31"`,
 * `"31/12"`, `"12-31"`.
 *
 * Needed in two places that both read what people have already written: the
 * backfill, which converts the free-text column every existing company carries,
 * and the importer, which meets spreadsheets typed by hand.
 *
 * Returns null rather than guessing. A year-end that cannot be read is a row for
 * somebody to look at — defaulting it to 31 December would hand a company a
 * financial year it does not have, and the roll would then carry that wrong date
 * forward a year at a time with nothing to reveal it.
 */
export function parseFyEndText(text: string | null | undefined): string | null {
  if (!text) return null;
  const clean = text.trim().toLowerCase().replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
  if (!clean) return null;

  // Already MM-DD.
  const md = MONTH_DAY.exec(clean);
  if (md) {
    try {
      return validateFyEndMd(clean);
    } catch {
      return null;
    }
  }

  const monthIndex = (word: string): number =>
    MONTH_NAMES.findIndex((name) => name.toLowerCase().startsWith(word.slice(0, 3))) + 1;

  // "31 december", "december 31", "31 dec", "dec 31"
  const words = clean.split(/[\s/-]+/).filter(Boolean);
  if (words.length === 2) {
    const [a, b] = words;
    const dayFirst = /^\d{1,2}$/.test(a);
    const day = Number(dayFirst ? a : b);
    const month = /^\d{1,2}$/.test(dayFirst ? b : a)
      ? Number(dayFirst ? b : a)
      : monthIndex(dayFirst ? b : a);
    if (!month || !day) return null;
    try {
      return validateFyEndMd(`${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    } catch {
      return null;
    }
  }

  // A bare month means its last day — "december" is a 31 December year-end.
  if (words.length === 1) {
    const month = monthIndex(words[0]);
    if (!month) return null;
    return `${String(month).padStart(2, "0")}-${daysInFyMonth(month)}`;
  }

  return null;
}

/**
 * How far ahead "due soon" looks, when no setting says otherwise.
 *
 * A default, not a constant: the AFR settings row overrides it, in the same way
 * a leave type's allowance is a default the employee's own row overrides. Three
 * months is the window the dashboard was asked for.
 */
export const DEFAULT_AFR_HORIZON_DAYS = 90;

/** A stored period, as the edit rules need to see it. */
export interface StoredAfrPeriod {
  period_end: string;
  afr_due_date: string;
  afr_filing_date: string | null;
  status: string;
}

/** What an edit to a stored period is trying to change. */
export interface AfrPeriodPatch {
  period_end?: string;
  afr_due_date?: string;
  afr_filing_date?: string | null;
}

/**
 * Why an edit to a stored financial year was refused, or null if it stands.
 *
 * Every way a period can legitimately be corrected after the fact, and the
 * handful of ways it cannot:
 *
 *   due date, open period      an extension. Always allowed.
 *   due date, filed period     a correction. Allowed, even to before the date
 *                              the return was filed — that year then reads as
 *                              filed late, which a filing can be.
 *   filing date, filed period  a correction. Allowed, within the same window a
 *                              filing had to satisfy in the first place.
 *   filing date, open period   refused. Setting it is *filing*, which also has
 *                              to open the next year — see `fileAfrReturn`. A
 *                              patch would do half the job and leave the company
 *                              with nothing open.
 *   clearing a filing date     refused. Un-filing has to remove the successor
 *                              the roll created, which a column patch cannot
 *                              reach — see `reopenAfrPeriod`.
 *   year-end                   a rekeying, allowed, but the filing date and the
 *                              deadline both have to still make sense against
 *                              the year they now describe.
 *
 * Shared so the dialog can grey out the button for the same reason the API
 * rejects the request, rather than the two drifting into disagreement about
 * which corrections are legal.
 */
export function periodPatchProblem(
  stored: StoredAfrPeriod,
  patch: AfrPeriodPatch,
  today: string,
): string | null {
  const touching = (key: keyof AfrPeriodPatch) => Object.prototype.hasOwnProperty.call(patch, key);

  const filed = stored.afr_filing_date != null;

  if (touching("afr_filing_date")) {
    if (patch.afr_filing_date == null) {
      return filed
        ? "Un-filing a return also has to remove the financial year it opened — reopen it instead"
        : "This year has no filing date to clear";
    }
    if (!filed) {
      return "Recording a filing date also opens the next financial year — file the return instead";
    }
  }

  if (stored.status === "waived" && (touching("afr_filing_date") || touching("afr_due_date"))) {
    return "This year is waived; reopen it before changing its dates";
  }

  const periodEnd = patch.period_end ?? stored.period_end;
  const dueDate = patch.afr_due_date ?? stored.afr_due_date;
  const filingDate =
    patch.afr_filing_date === undefined ? stored.afr_filing_date : patch.afr_filing_date;

  // The deadline always falls after the year it closes. A due date before the
  // year-end is a deadline for some other year, and the roll would carry it
  // forward wrongly every year after.
  if (dueDate < periodEnd) {
    return `A return for the year ending ${periodEnd} cannot be due before that date`;
  }

  if (filingDate) {
    const problem = filingDateProblem(filingDate, { period_end: periodEnd }, today);
    if (problem) return problem;
  }

  return null;
}
